import { useState, type ReactNode } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarHeader,
  SidebarGroupLabel,
  SidebarMenuButton,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Plus, BookCheck, Search, Settings2, UsersIcon } from "lucide-react";
import { useUser } from "@clerk/react";
import { Separator } from "@/components/ui/separator";
import { Link, useLocation } from "react-router-dom";
import { TeamSwitcher } from "./team-switcher";
import { useDashboardStore, useSearchStore, useSidebarPrefsStore } from "@/stores";
import type { SidebarMenuKey } from "@/stores/sidebar/sidebarPrefsStore";
import PlanCardSidebar from "./plan-card";
import { NavUser } from "./nav-user";
import { CampaignsNav } from "./campaigns-nav";
import { AmyNav } from "./amy-nav";
import { SettingsNav } from "./settings-nav";
import { SidebarSettingsDialog } from "./sidebar-settings-dialog";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { useComplianceRole } from "@/hooks/IsAdminContext";
import { useRadiusPreset } from "@/hooks/use-radius-preset";

export function AppSidebar() {
  const { user } = useUser();
  const location = useLocation();
  const { tasks } = useDashboardStore();
  const { setIsOpen } = useSearchStore();
  // Compliance reviewers see only the Tasks entry (plus org switch/sign-out).
  const { isCompliance } = useComplianceRole();
  // Main Menu row order is a per-device preference (gear next to the label).
  const menuOrder = useSidebarPrefsStore((s) => s.prefs.order);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const isTasksActive = location.pathname.includes("/dashboard/tasks");
  const isCrmActive = location.pathname.startsWith("/dashboard/crm");
  // Staff-only pages (Success, Batch status) live in the /manager area —
  // see manager-sidebar.tsx and the header's AreaSwitch.

  // Every Main Menu row by key; rendered in the user's saved order.
  const menuRows: Record<SidebarMenuKey, ReactNode> = {
    search: (
      <SidebarMenuItem key="search">
        <SidebarMenuButton
          onClick={() => setIsOpen(true)}
          tooltip="Search (⌘K)"
          className="cursor-pointer"
        >
          <Search />
          <span>Search</span>
          <span className="ml-auto text-xs text-muted-foreground">⌘K</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    ),
    campaigns: <CampaignsNav key="campaigns" />,
    amy: <AmyNav key="amy" />,
    // Every campaign creator in the org, one table.
    crm: (
      <SidebarMenuItem key="crm">
        <SidebarMenuButton asChild isActive={isCrmActive} tooltip="Global CRM">
          <Link to="/dashboard/crm">
            <UsersIcon />
            <span>Global CRM</span>
            {/* <span className={`ml-auto text-[11px] -rotate-6 rounded-sm px-1 py-0.25 ${isCrmActive ? "bg-info text-white" : "bg-info/10 text-info"}`}>
              New
            </span> */}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    ),
    tasks: (
      <SidebarMenuItem key="tasks">
        <SidebarMenuButton asChild isActive={isTasksActive} tooltip="Tasks">
          <Link to="/dashboard/tasks">
            <BookCheck />
            <span>Tasks</span>
            {tasks.length > 0 && (
              <div
                className={`ml-auto rounded-sm size-4.5 -mr-0.25 flex items-center justify-center text-[11px] font-mono
                  ${isTasksActive ? "bg-primary text-white" : "bg-primary/10 text-primary"}
                `}
              >
                {tasks.length}
              </div>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    ),
    settings: <SettingsNav key="settings" />,
  };

  const visibleRows: SidebarMenuKey[] = isCompliance ? ["tasks"] : menuOrder;

  // Full radius: the page becomes a rounded card on the tinted frame
  // (SidebarInset in Dashboard.tsx); the other presets stay docked.
  const radius = useRadiusPreset();

  return (
    <Sidebar collapsible="icon" variant={radius === "full" ? "inset" : "sidebar"}>
      <SidebarHeader>
        <ScopeWrapper>
          <TeamSwitcher />
        </ScopeWrapper>
      </SidebarHeader>
      <SidebarContent className="overflow-x-hidden">
        <SidebarGroup>
          <SidebarGroupLabel>Main Menu</SidebarGroupLabel>
          {/* Customize gear — revealed on sidebar hover (always shown on mobile,
              where there is no hover). Nothing to customize for compliance. */}
          {!isCompliance && (
            <SidebarGroupAction
              title="Customize sidebar"
              aria-label="Customize sidebar"
              onClick={() => setCustomizeOpen(true)}
              // Ghost-button look applied directly: SidebarGroupAction is already
              // a <button>, so nesting ui/Button inside it would be <button><button>.
              className="size-6 shrink-0 cursor-pointer text-sidebar-foreground/60 hover:bg-accent hover:text-accent-foreground [&>svg]:size-3.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 max-lg:opacity-100"
            >
              <Settings2 />
              <span className="sr-only">Customize sidebar</span>
            </SidebarGroupAction>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleRows.map((key) => menuRows[key])}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {!isCompliance && (
          <>
            {/* Inset (Full): line up with the menu items instead of running
                into the content card's edge. Same on plan-card's dividers. */}
            <Separator className="group-data-[variant=inset]:mx-2 group-data-[variant=inset]:w-auto!" />

            <Link to="/dashboard/campaigns/create" className="px-2">
              <SidebarMenuButton
                tooltip="Create Campaign"
                isActive={location.pathname === "/dashboard/campaigns/create"}
              >
                <Plus className="h-4 w-4" /> Create Campaign
              </SidebarMenuButton>
            </Link>

            <SidebarGroup className="h-full w-full flex flex-col justify-end items-start p-0">
              <PlanCardSidebar />
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        <ScopeWrapper>
          <NavUser
            user={{
              name: `${user?.firstName} ${user?.lastName}`,
              email: `${user?.primaryEmailAddress?.emailAddress}`,
              avatar: `${user?.imageUrl}`,
            }}
          />
        </ScopeWrapper>
      </SidebarFooter>
      <SidebarRail />

      {!isCompliance && (
        <SidebarSettingsDialog open={customizeOpen} onOpenChange={setCustomizeOpen} />
      )}
    </Sidebar>
  );
}
